// /app/frontend/src/components/handshake/OrderHandshakeQR.js
// Componente para exibir QR Codes de Handshake do pedido
import React, { useState, useEffect, useCallback } from 'react';
import { QRCodeSVG } from 'qrcode.react';
import { 
  FiRefreshCw, FiCopy, FiCheck, FiClock, FiShield,
  FiAlertTriangle, FiDownload
} from 'react-icons/fi';
import api from '@/services/api';

const OrderHandshakeQR = ({ orderId, userType, onClose }) => {
  const [handshake, setHandshake] = useState(null);
  const [loading, setLoading] = useState(true);
  const [regenerating, setRegenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [timeLeft, setTimeLeft] = useState(null);
  const [error, setError] = useState('');

  const loadHandshake = useCallback(async () => {
    try {
      const response = await api.get(`/orders/${orderId}/handshake`);
      setHandshake(response.data);
      setError('');
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao carregar QR Code');
    } finally {
      setLoading(false);
    }
  }, [orderId]);

  useEffect(() => {
    loadHandshake();
    const interval = setInterval(loadHandshake, 30000); // Atualiza a cada 30s
    return () => clearInterval(interval);
  }, [loadHandshake]);

  // Contador de tempo
  useEffect(() => {
    if (!handshake?.qr_expiry_minutes) return;
    
    const checkExpiry = () => {
      // Simplificado - QR tem 5 minutos de validade
      const expired = handshake.client_qr_expired || handshake.product_qr_expired;
      if (expired) {
        setTimeLeft(0);
      } else {
        setTimeLeft(handshake.qr_expiry_minutes * 60);
      }
    };
    
    checkExpiry();
    const interval = setInterval(() => {
      setTimeLeft(prev => prev > 0 ? prev - 1 : 0);
    }, 1000);
    
    return () => clearInterval(interval);
  }, [handshake]);

  const handleRegenerate = async (qrType) => {
    setRegenerating(true);
    try {
      await api.post(`/orders/${orderId}/handshake/regenerate-qr`, {
        order_id: orderId,
        qr_type: qrType
      });
      await loadHandshake();
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao regenerar QR');
    } finally {
      setRegenerating(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Erro ao copiar:', err);
    }
  };

  const downloadQR = (qrId, filename) => {
    const svg = document.getElementById(qrId);
    if (!svg) return;
    
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    const img = new Image();
    
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      ctx.drawImage(img, 0, 0);
      
      const link = document.createElement('a');
      link.download = filename;
      link.href = canvas.toDataURL('image/png');
      link.click();
    };
    
    img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
  };

  const formatTime = (seconds) => {
    if (seconds <= 0) return 'Expirado';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-red-500 border-t-transparent"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-xl">
        <p className="text-red-700 flex items-center gap-2">
          <FiAlertTriangle />
          {error}
        </p>
        <button
          onClick={loadHandshake}
          className="mt-2 text-sm text-red-600 hover:text-red-800"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // Renderização baseada no tipo de usuário
  return (
    <div className="space-y-4" data-testid="handshake-qr">
      <div className="flex items-center justify-between">
        <h3 className="font-bold text-gray-800 flex items-center gap-2">
          <FiShield className="text-green-500" />
          Verificação de Segurança
        </h3>
        {onClose && (
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        )}
      </div>

      {/* CLIENTE - Mostra seu QR Code para o entregador */}
      {userType === 'cliente' && handshake && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
            <p className="text-sm text-blue-700">
              <strong>Mostre este QR Code ao entregador</strong> quando ele chegar para confirmar que você é o destinatário correto.
            </p>
          </div>

          <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
            {handshake.client_qr_expired ? (
              <div className="py-8">
                <FiAlertTriangle className="mx-auto text-4xl text-yellow-500 mb-2" />
                <p className="text-gray-600">QR Code expirado</p>
                <button
                  onClick={() => handleRegenerate('client')}
                  disabled={regenerating}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {regenerating ? 'Regenerando...' : 'Gerar Novo QR'}
                </button>
              </div>
            ) : (
              <>
                <QRCodeSVG
                  id="client-qr"
                  value={handshake.client_qr_token}
                  size={200}
                  level="H"
                  includeMargin
                  className="mx-auto"
                />
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <FiClock />
                  <span>Expira em: {formatTime(timeLeft)}</span>
                </div>
              </>
            )}
          </div>

          {/* PIN de Backup */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm text-yellow-700 mb-2">
              <strong>PIN de Backup</strong> (use se não conseguir escanear o QR):
            </p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl font-mono font-bold tracking-widest text-gray-800">
                {handshake.backup_pin}
              </span>
              <button
                onClick={() => copyToClipboard(handshake.backup_pin)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                {copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
              </button>
            </div>
          </div>

          {/* Status */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Status da Verificação:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${handshake.dropoff_client_verified ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="text-sm text-gray-600">Seu QR verificado pelo entregador</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${handshake.dropoff_product_verified ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="text-sm text-gray-600">Produto verificado por você</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LOJISTA - Mostra QR Code do produto para o entregador bipar */}
      {userType === 'lojista' && handshake && (
        <div className="space-y-4">
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-sm text-purple-700">
              <strong>QR Code do Produto</strong> - O entregador deve escanear este código para confirmar a coleta.
            </p>
          </div>

          <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-6 text-center">
            {handshake.product_qr_expired ? (
              <div className="py-8">
                <FiAlertTriangle className="mx-auto text-4xl text-yellow-500 mb-2" />
                <p className="text-gray-600">QR Code expirado</p>
                <button
                  onClick={() => handleRegenerate('product')}
                  disabled={regenerating}
                  className="mt-3 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                >
                  {regenerating ? 'Regenerando...' : 'Gerar Novo QR'}
                </button>
              </div>
            ) : (
              <>
                <QRCodeSVG
                  id="product-qr"
                  value={handshake.product_qr_token}
                  size={200}
                  level="H"
                  includeMargin
                  className="mx-auto"
                />
                <div className="mt-4 flex items-center justify-center gap-2 text-sm text-gray-500">
                  <FiClock />
                  <span>Expira em: {formatTime(timeLeft)}</span>
                </div>
                <button
                  onClick={() => downloadQR('product-qr', `pedido_${orderId}_produto.png`)}
                  className="mt-3 flex items-center gap-2 mx-auto px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
                >
                  <FiDownload size={16} />
                  Baixar para Imprimir
                </button>
              </>
            )}
          </div>

          {/* PIN de Backup */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4">
            <p className="text-sm text-yellow-700 mb-2">
              <strong>PIN de Backup</strong>:
            </p>
            <div className="flex items-center justify-center gap-2">
              <span className="text-3xl font-mono font-bold tracking-widest text-gray-800">
                {handshake.backup_pin}
              </span>
              <button
                onClick={() => copyToClipboard(handshake.backup_pin)}
                className="p-2 text-gray-500 hover:text-gray-700"
              >
                {copied ? <FiCheck className="text-green-500" /> : <FiCopy />}
              </button>
            </div>
          </div>

          {/* Status da Coleta */}
          <div className="bg-gray-50 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-700 mb-2">Status da Coleta:</p>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${handshake.pickup_store_scanned ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="text-sm text-gray-600">Entregador na loja</span>
              </div>
              <div className="flex items-center gap-2">
                <span className={`w-3 h-3 rounded-full ${handshake.pickup_product_scanned ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                <span className="text-sm text-gray-600">Produto escaneado</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ENTREGADOR - Mostra status das validações e QR do produto para mostrar ao cliente */}
      {userType === 'entregador' && handshake && (
        <div className="space-y-4">
          <div className="bg-green-50 border border-green-200 rounded-xl p-4">
            <p className="text-sm text-green-700">
              Siga os passos de verificação para garantir uma entrega segura.
            </p>
          </div>

          {/* Status Geral */}
          <div className="grid grid-cols-2 gap-3">
            <div className={`p-4 rounded-xl border ${handshake.pickup_verified ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className="text-xs text-gray-500">Coleta</p>
              <p className={`font-bold ${handshake.pickup_verified ? 'text-green-600' : 'text-gray-400'}`}>
                {handshake.pickup_verified ? '✓ Concluída' : 'Pendente'}
              </p>
            </div>
            <div className={`p-4 rounded-xl border ${handshake.dropoff_product_verified ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'}`}>
              <p className="text-xs text-gray-500">Entrega</p>
              <p className={`font-bold ${handshake.dropoff_product_verified ? 'text-green-600' : 'text-gray-400'}`}>
                {handshake.dropoff_product_verified ? '✓ Concluída' : 'Pendente'}
              </p>
            </div>
          </div>

          {/* QR do Produto para mostrar ao cliente */}
          {handshake.product_qr_token && (
            <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-4 text-center">
              <p className="text-sm text-gray-600 mb-3">
                Mostre este QR para o cliente confirmar o recebimento:
              </p>
              <QRCodeSVG
                value={handshake.product_qr_token}
                size={150}
                level="H"
                includeMargin
                className="mx-auto"
              />
            </div>
          )}

          {/* Etapas detalhadas */}
          <div className="space-y-2">
            <p className="text-sm font-medium text-gray-700">Etapas:</p>
            {[
              { key: 'pickup_store_scanned', label: '1. Escanear QR da loja' },
              { key: 'pickup_product_scanned', label: '2. Escanear QR do produto' },
              { key: 'dropoff_arrived', label: '3. Confirmar chegada no cliente' },
              { key: 'dropoff_client_verified', label: '4. Escanear QR do cliente' },
              { key: 'dropoff_product_verified', label: '5. Cliente confirma produto' }
            ].map(step => (
              <div key={step.key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-lg">
                <span className={`w-5 h-5 rounded-full flex items-center justify-center text-xs ${handshake[step.key] ? 'bg-green-500 text-white' : 'bg-gray-300 text-gray-600'}`}>
                  {handshake[step.key] ? '✓' : ''}
                </span>
                <span className={`text-sm ${handshake[step.key] ? 'text-green-700' : 'text-gray-600'}`}>
                  {step.label}
                </span>
              </div>
            ))}
          </div>

          {/* Raios de validação */}
          <div className="bg-blue-50 border border-blue-200 rounded-xl p-3">
            <p className="text-xs text-blue-700">
              📍 Raio de coleta: <strong>{handshake.pickup_radius_meters}m</strong> | 
              Raio de entrega: <strong>{handshake.dropoff_radius_meters}m</strong>
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrderHandshakeQR;
